import React from "react";
import { Edit } from "lucide-react";
import type { ExpenseBreakdownPanelProps } from "./ExpenseBreakdownPanel.types";

export const ExpenseBreakdownPanel: React.FC<ExpenseBreakdownPanelProps> = ({
  expensesByCategory,
  expenseCategories,
  currentSnapshot,
  maxExpenseCategoryTotal,
  calculateActualExpenseAmount,
  onExpenseClick,
}) => {
  return (
    <>
      <h4 className="mb-4 font-medium text-expense-muted">
        Monthly Expenses ($
        {currentSnapshot?.monthlyBurn.toFixed(2) || "0.00"})
      </h4>

      {expenseCategories.length === 0 ? (
        <div className="text-sm text-white/45">No expenses at this time</div>
      ) : (
        <div className="space-y-3">
          {expenseCategories.map((category) => {
            const categoryExpenses = expensesByCategory[category];
            const categoryTotal = categoryExpenses.reduce(
              (sum, exp) => sum + calculateActualExpenseAmount(exp),
              0,
            );
            const categoryWidth =
              (categoryTotal / maxExpenseCategoryTotal) * 100;

            return (
              <div key={`expense-${category}`} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-expense-muted">
                    {category}
                  </span>
                  <span className="text-sm text-expense">
                    -${categoryTotal.toFixed(2)}
                  </span>
                </div>

                <div className="h-2 w-full rounded-full bg-expense/15">
                  <div
                    className="h-2 rounded-full bg-gradient-to-r from-expense-muted to-expense transition-all duration-300"
                    style={{ width: `${categoryWidth}%` }}
                  />
                </div>

                <div className="ml-2 space-y-1">
                  {categoryExpenses.map((expense) => (
                    <div
                      key={expense.id}
                      className="group flex cursor-pointer items-center justify-between rounded p-2 text-xs text-expense transition-all duration-200 hover:scale-[1.02] hover:bg-expense/10 hover:text-[#e2cdd8]"
                      onClick={() => onExpenseClick?.(expense.id)}
                      title={`Click to edit: ${expense.name}`}
                    >
                      <span className="transition-all duration-200 group-hover:font-medium">
                        {expense.name}
                      </span>
                      <div className="flex items-center gap-1">
                        <span>
                          -{expense.isOneTime ? "♦" : "$"}
                          {calculateActualExpenseAmount(
                            expense,
                          ).toFixed(2)}
                          {expense.isOneTime ? " (one-time)" : ""}
                        </span>
                        <span className="text-white/40 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                          <Edit className="h-3 w-3" />
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};
